import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faIdCard, faShieldAlt, faCalendarAlt, faUserGroup, faExternalLinkAlt, faChartLine, faPhone, faTag, faList, faCheckCircle, faTimesCircle, faClock, faGift, faBell } from '@/fontawesome';
import { formatDate, formatRelativeTime } from '@/utils/helpers';
import FeatureIcons from '@/components/admin/common/FeatureIcons';
import SubscriptionProductTypeIcons from '@/components/admin/common/SubscriptionProductTypeIcons';
import EmailOptOutIcons from '@/components/admin/common/EmailOptOutIcons';
import type { User } from '@/models';

interface UserDetailPageTabDetailsProps {
  user: User;
}

export default function UserDetailPageTabDetails({ user }: UserDetailPageTabDetailsProps) {
  return (
    <div className="space-y-8">
      {/* User Information */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
          <FontAwesomeIcon icon={faIdCard} className="w-6 h-6 mr-3" />
          Benutzerinformationen
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

          <div className="col-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Benutzer-ID
            </label>
            <p className="text-gray-900 dark:text-white">{user.id}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FontAwesomeIcon icon={faUser} className="w-4 h-4 mr-2" />
              Name
            </label>
            <p className="text-gray-900 dark:text-white font-medium">{user.firstName} {user.lastName}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 mr-2" />
              E-Mail
            </label>
            <p className="text-gray-900 dark:text-white">{user.email}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FontAwesomeIcon icon={faPhone} className="w-4 h-4 mr-2" />
              Telefonnummer
            </label>
            <p className="text-gray-900 dark:text-white">{user.phone}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FontAwesomeIcon icon={faGift} className="w-4 h-4 mr-2" />
              Empfehlungscode
            </label>
            <p className="text-gray-900 dark:text-white font-mono">
              {user.referrerCode !== '' ? user.referrerCode : '—'}
            </p>
          </div>

          <div className="col-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FontAwesomeIcon icon={faShieldAlt} className="w-4 h-4 mr-2" />
              Rolle
            </label>
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
              user.permissionGroup === 'ADMIN'
                ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400'
                : user.permissionGroup === 'EMPLOYEE'
                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-400'
                : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
            }`}>
              {user.permissionGroup === 'ADMIN' ? 'Administrator' : user.permissionGroup === 'EMPLOYEE' ? 'Mitarbeiter' : 'Benutzer'}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FontAwesomeIcon icon={faUserGroup} className="w-4 h-4 mr-2" />
              Community-Mitglied
            </label>
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
              user.featureConfig.communityMember
                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-400'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
            }`}>
              {user.featureConfig.communityMember ? 'Ja' : 'Nein'}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FontAwesomeIcon icon={faBell} className="w-4 h-4 mr-2" />
              Push-Benachrichtigungen (App)
            </label>
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
              user.firebaseUID !== null
                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-400'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
            }`}>
              {user.firebaseUID !== null ? 'Aktiv' : 'Inaktiv'}
            </span>
          </div>

          <div className="col-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-2" />
              Erstellt
            </label>
            <p className="text-gray-900 dark:text-white">{formatDate(user.created)}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Zuletzt aktualisiert
            </label>
            <p className="text-gray-900 dark:text-white">
              {user.updated !== null ? formatDate(user.updated) : 'Nie'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Zuletzt aktiv
            </label>
            <p className="text-gray-900 dark:text-white">
              {formatRelativeTime(user.lastActive)}
              <span className="text-gray-500 dark:text-gray-400 ml-1">
                ({formatDate(user.lastActive)})
              </span>
            </p>
          </div>

          <div className="col-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Aktive Abonnements
            </label>
            <SubscriptionProductTypeIcons
              productTypes={user.activeSubscriptionProductTypes}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Aktive Features
            </label>
            <FeatureIcons featureConfig={user.featureConfig} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              E-Mail-Benachrichtigungen
            </label>
            <EmailOptOutIcons emailOptOut={user.emailOptOut} />
          </div>

        </div>
      </div>

      {/* ConvertKit Stats */}
      {user.convertkitStats !== null && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <FontAwesomeIcon icon={faChartLine} className="w-6 h-6 mr-3" />
              ConvertKit Statistiken
            </h2>
            {user.convertkitSubscriberID !== null && (
              <a
                href={`https://app.kit.com/subscribers/${user.convertkitSubscriberID}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/30 cursor-pointer"
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} className="w-4 h-4 mr-2" />
                In ConvertKit öffnen
              </a>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">Gesendet</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{user.convertkitStats.sent}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">Geöffnet</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {user.convertkitStats.opened}
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">
                  ({(user.convertkitStats.openRate * 100).toFixed(1)}%)
                </span>
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">Geklickt</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {user.convertkitStats.clicked}
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">
                  ({(user.convertkitStats.clickRate * 100).toFixed(1)}%)
                </span>
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">Bounced</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{user.convertkitStats.bounced}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">Zuletzt gesendet</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {user.convertkitStats.lastSent ? formatDate(user.convertkitStats.lastSent) : '-'}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">Zuletzt geöffnet</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {user.convertkitStats.lastOpened ? formatDate(user.convertkitStats.lastOpened) : '-'}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">Zuletzt geklickt</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {user.convertkitStats.lastClicked ? formatDate(user.convertkitStats.lastClicked) : '-'}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">Sends seit letztem Open/Click</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {user.convertkitStats.sendsSinceLastOpen} / {user.convertkitStats.sendsSinceLastClick}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ConvertKit Link (when stats not available but subscriber ID exists) */}
      {user.convertkitStats === null && user.convertkitSubscriberID !== null && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <FontAwesomeIcon icon={faChartLine} className="w-6 h-6 mr-3" />
              ConvertKit
            </h2>
            <a
              href={`https://app.kit.com/subscribers/${user.convertkitSubscriberID}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/30 cursor-pointer"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} className="w-4 h-4 mr-2" />
              In ConvertKit öffnen
            </a>
          </div>
        </div>
      )}

      {/* Keine ConvertKit Daten existieren */}
      {user.convertkitStats === null && user.convertkitSubscriberID === null && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <FontAwesomeIcon icon={faChartLine} className="w-6 h-6 mr-3" />
              ConvertKit
            </h2>
          </div>
          <div className="text-center py-8">
            <FontAwesomeIcon icon={faChartLine} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 mb-2 font-medium">
              Keine Convertkit Daten vorhanden
            </p>
          </div>
        </div>
      )}

      {/* Brevo - Contact data exists */}
      {user.brevoId !== null && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <FontAwesomeIcon icon={faEnvelope} className="w-6 h-6 mr-3" />
              Brevo
            </h2>
            <a
              href={`https://app.brevo.com/contact/index/${user.brevoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/30 cursor-pointer"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} className="w-4 h-4 mr-2" />
              In Brevo öffnen
            </a>
          </div>

          {/* Sync Status */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                <FontAwesomeIcon icon={faClock} className="w-3 h-3 mr-1" />
                Letzte Synchronisierung
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {user.lastBrevoSync !== null ? formatDate(user.lastBrevoSync) : 'Nie'}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">Sync-Status</p>
              <p className={`text-sm font-medium ${user.lastBrevoSyncSuccess ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                <FontAwesomeIcon
                  icon={user.lastBrevoSyncSuccess ? faCheckCircle : faTimesCircle}
                  className="w-4 h-4 mr-1"
                />
                {user.lastBrevoSyncSuccess ? 'Erfolgreich' : 'Fehlgeschlagen'}
              </p>
            </div>
          </div>

          {/* Brevo Fields */}
          {Object.keys(user.brevoFields).length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                <FontAwesomeIcon icon={faTag} className="w-4 h-4 mr-2" />
                Brevo Felder
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(user.brevoFields).map(([key, value]) => (
                  <div key={key} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">{key}</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white break-words">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Brevo Lists */}
          {user.brevoLists.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                <FontAwesomeIcon icon={faList} className="w-4 h-4 mr-2" />
                Brevo Listen
              </h3>
              <div className="flex flex-wrap gap-2">
                {user.brevoLists.map((listId) => (
                  <span
                    key={listId}
                    className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-400"
                  >
                    Liste {listId}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Brevo - No data */}
      {user.brevoId === null && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <FontAwesomeIcon icon={faEnvelope} className="w-6 h-6 mr-3" />
              Brevo
            </h2>
          </div>
          <div className="text-center py-8">
            <FontAwesomeIcon icon={faEnvelope} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 mb-2 font-medium">
              Keine Brevo Daten vorhanden
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
